import { useEffect, useState } from 'react';
import { BookOpen, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { articles as staticArticles, type Article } from '@/data/articles';

const formatDate = (d: string | Date) =>
  new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

/**
 * Loads static + admin-authored + AutoSEO blog articles, merged into one list.
 * - Static: src/data/articles.tsx
 * - Admin-authored: `custom_articles` table
 * - AutoSEO webhook: `blog_posts` table (rendered as HTML via content field)
 */
export function useAllArticles() {
  const [articles, setArticles] = useState<Article[]>(staticArticles);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [customRes, blogRes, viewsRes] = await Promise.all([
        supabase
          .from('custom_articles')
          .select('*')
          .eq('published', true)
          .order('publish_date', { ascending: false }),
        supabase
          .from('blog_posts')
          .select('id, title, slug, content_html, content_markdown, hero_image_url, hero_image_alt, infographic_url, meta_description, reading_time, published_at, updated_at, faq_schema, tags')
          .order('published_at', { ascending: false }),
        (supabase.rpc as any)('get_article_view_counts') as Promise<{
          data: { content_slug: string; view_count: number }[] | null;
          error: Error | null;
        }>,
      ]);

      if (!mounted) return;

      const viewCounts: Record<string, number> = {};
      if (!viewsRes.error && viewsRes.data) {
        for (const row of viewsRes.data) {
          if (!row.content_slug) continue;
          viewCounts[row.content_slug] = Number(row.view_count ?? 0);
        }
      }

      const mappedCustom: Article[] = !customRes.error && customRes.data
        ? customRes.data.map((row) => ({
            id: `db-${row.id}`,
            title: row.title,
            excerpt: row.excerpt,
            category: row.category,
            readTime: row.read_time,
            date: formatDate(row.publish_date),
            icon: BookOpen,
            slug: row.slug,
            metaDescription: row.meta_description,
            content: row.content,
          }))
        : [];

      const mappedBlog: Article[] = !blogRes.error && blogRes.data
        ? blogRes.data.map((row) => {
            const excerpt = (row.meta_description ?? '').slice(0, 160);
            const minutes = row.reading_time ?? 5;
            const rawMd = row.content_markdown ?? row.content_html ?? '';
            // Strip raw HTML anchor jump-tags (e.g. <a name="..."></a>) that
            // AutoSEO embeds for TOC linking - ReactMarkdown shows them as literal text.
            const cleaned = rawMd
              .replace(/<a\s+name=["'][^"']*["']\s*>\s*<\/a>/gi, '')
              .replace(/<a\s+id=["'][^"']*["']\s*>\s*<\/a>/gi, '');
            return {
              id: `blog-${row.id}`,
              title: row.title,
              excerpt,
              category: 'Exam Tips',
              readTime: `${minutes} min read`,
              date: formatDate(row.published_at ?? row.updated_at ?? new Date()),
              icon: Sparkles,
              slug: row.slug,
              metaDescription: row.meta_description ?? excerpt,
              // Prefer Markdown for the existing ReactMarkdown renderer; fall back to HTML.
              content: cleaned,
              isHtml: !row.content_markdown && !!row.content_html,
              heroImageUrl: row.hero_image_url ?? undefined,
              heroImageAlt: row.hero_image_alt ?? undefined,
              infographicUrl: row.infographic_url ?? undefined,
              faqSchema: (row.faq_schema as { question: string; answer: string }[] | null) ?? undefined,
            } as Article;
          })
        : [];

      const seen = new Set<string>();
      const merged: Article[] = [];
      for (const a of [...mappedBlog, ...mappedCustom, ...staticArticles]) {
        if (seen.has(a.slug)) continue;
        seen.add(a.slug);
        merged.push({ ...a, viewCount: viewCounts[a.slug] ?? 0 });
      }
      setArticles(merged);
      setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  return { articles, loading };
}
